import React, {useContext} from "react";
import {Box, Button, Stack, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Add01Icon} from "@hugeicons/core-free-icons";
import {SignInContext} from "../../../context/SignInContext";
import {ApiKeyRepository} from "../../../repositories/ApiKeyRepository";
import {ApiKeysTable} from "./api-keys-table";
import {useApiKeys} from "../useApiKeys";
import {canCreateOrganizationApiKey, canManageApiKey} from "../apiKeyAccess";

const headerButtonSx = {
  height: 32, px: 1.5, borderRadius: "10px", textTransform: "none", fontSize: 12.5, fontWeight: 700,
  color: "#374151", bgcolor: "#FAFAFD", "&:hover": {bgcolor: "#ECECF3"},
};

// API keys of one organization. There is no per-organization endpoint, so this filters the full list (admin)
// or the user's own keys (everyone else). Only the admin can reveal, copy, change and revoke keys, and only
// keys they own or of organizations they are a member of; a manager can only create keys for their organizations.
export const OrganizationApiKeys = ({organization}) => {
  const {currentUser} = useContext(SignInContext);
  const isAdmin = currentUser?.roles?.includes("admin");
  const canCreate = canCreateOrganizationApiKey(organization, currentUser);

  const {apiKeys, openCreate, dialogs, tableProps} = useApiKeys({
    fetchKeys: async () => {
      const response = isAdmin ? await ApiKeyRepository.getAll() : await ApiKeyRepository.getMyKeys();
      return (response.data || []).filter(apiKey => apiKey.organizationId === organization.id);
    },
    deps: [organization.id, isAdmin],
    organizationId: organization.id,
  });

  return (
    <Box>
      {dialogs}
      <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} gap={2} sx={{mb: 1}}>
        <Typography variant={"subtitle2"} fontWeight={600}>
          API Keys{!tableProps.loading && ` (${apiKeys.length})`}
        </Typography>
        {canCreate && (
          <Button
            sx={{...headerButtonSx, textWrap: "nowrap"}}
            startIcon={<HugeiconsIcon icon={Add01Icon} size={16}/>}
            onClick={openCreate}
          >
            Create Key
          </Button>
        )}
      </Stack>
      <ApiKeysTable
        {...tableProps}
        size={"small"}
        hideOrganization
        readOnly={!isAdmin}
        canManageKey={apiKey => canManageApiKey(apiKey, currentUser, [organization])}
        emptyText={`No API keys for ${organization.name} yet`}
      />
    </Box>
  );
};
